import React, { useState, Children } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { BookOpen, Check, ArrowRight, Heart, Sparkles, Star, MessageCircle, Calendar, DollarSign, Users, Rocket, ShieldCheck, Award, Zap, X, Mail, Phone, MapPin, HelpCircle } from 'lucide-react';
// Animation variants
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.1
    }
  }
};
const scaleIn = {
  hidden: {
    opacity: 0,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.9,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
export function VendeSinVenderLanding() {
  const {
    scrollYProgress
  } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, -100]);
  // Modal state
  const [activeModal, setActiveModal] = useState<'soporte' | 'contacto' | 'terminos' | 'privacidad' | null>(null);
  return <div className="min-h-screen w-full bg-[#F5EFE6] text-[#1D1D1F] font-sans selection:bg-[#D1A047]/20 overflow-x-hidden" data-id="element-3">
      {/* Premium Font Import */}
      <style data-id="element-4">{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Inter:wght@300;400;500;600;700&display=swap');
        
        .font-serif { font-family: 'Cormorant Garamond', serif; }
        .font-sans { font-family: 'Inter', sans-serif; }
      `}</style>

      {/* Luxury Navigation */}
      <nav className="fixed top-0 left-0 w-full z-50 bg-[#FAF7F3]/95 backdrop-blur-xl border-b border-[#D1A047]/20 transition-all duration-300" data-id="element-5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex justify-between items-center" data-id="element-6">
          <div className="flex items-center gap-2" data-id="element-7">
            <div className="w-8 h-8 bg-gradient-to-br from-[#D1A047] to-[#C9A76A] rounded-lg flex items-center justify-center" data-id="element-8">
              <BookOpen className="w-5 h-5 text-[#FAF7F3]" data-id="element-9" />
            </div>
            <span className="text-xl font-serif font-bold tracking-tight text-[#1D1D1F]" data-id="element-10">
              Vende sin Vender
            </span>
          </div>
        </div>
      </nav>

      {/* HERO SECTION - Premium Design */}
      <header className="relative pt-32 pb-24 px-6 md:pt-40 md:pb-32 overflow-hidden bg-gradient-to-br from-[#0F355E] via-[#0C1F36] to-[#3A2A22]" data-id="element-11">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D1A047]/10 rounded-full blur-[150px] pointer-events-none" data-id="element-12"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#C9A76A]/10 rounded-full blur-[120px] pointer-events-none" data-id="element-13"></div>

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-14">
          {/* Promotional Banner - Non-clickable */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="mb-16" data-id="element-15">
            <div className="relative overflow-hidden rounded-3xl border-2 border-[#D1A047]/30 shadow-2xl bg-gradient-to-br from-[#0F355E]/50 to-[#3A2A22]/50 backdrop-blur-sm p-8 md:p-12 text-center" data-id="element-16">
              <div className="absolute inset-0 bg-gradient-to-r from-[#D1A047]/5 via-[#C9A76A]/5 to-[#D1A047]/5" data-id="element-17"></div>
              <div className="relative z-10" data-id="element-18">
                <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#D1A047] to-[#C9A76A] rounded-full mb-6" data-id="element-19">
                  <Sparkles className="w-5 h-5 text-[#1D1D1F]" data-id="element-20" />
                  <span className="text-sm font-bold tracking-wide text-[#1D1D1F] uppercase" data-id="element-21">
                    Oferta de Lanzamiento
                  </span>
                  <Sparkles className="w-5 h-5 text-[#1D1D1F]" data-id="element-22" />
                </div>
                <h3 className="font-serif text-3xl md:text-5xl text-[#FAF7F3] mb-4 leading-tight" data-id="element-23">
                  Obtén el Pack Premium
                </h3>
                <p className="text-lg md:text-xl text-[#D8D1C6] max-w-2xl mx-auto" data-id="element-24">
                  7 recursos digitales exclusivos para transformar tu forma de
                  vender
                </p>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-25">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="space-y-8 text-center lg:text-left" data-id="element-26">
              <motion.div variants={fadeIn} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D1A047]/20 border border-[#D1A047]/30 backdrop-blur-sm" data-id="element-27">
                <Sparkles className="w-4 h-4 text-[#D1A047]" data-id="element-28" />
                <span className="text-sm font-semibold tracking-wide text-[#FAF7F3] uppercase" data-id="element-29">
                  Edición Premium
                </span>
              </motion.div>

              <motion.h1 variants={fadeIn} className="font-serif text-5xl md:text-7xl font-bold leading-[1.05] text-[#FAF7F3]" data-id="element-30">
                Pack Exclusivo
                <br data-id="element-31" />
                <span className="bg-gradient-to-r from-[#D1A047] via-[#C9A76A] to-[#D1A047] bg-clip-text text-transparent" data-id="element-32">
                  Vende sin Vender
                </span>
                <br data-id="element-33" />
                <span className="text-4xl md:text-5xl font-light italic" data-id="element-34">
                  7 en 1
                </span>
              </motion.h1>

              <motion.p variants={fadeIn} className="text-xl md:text-2xl text-[#D8D1C6] leading-relaxed font-light max-w-xl mx-auto lg:mx-0" data-id="element-35">
                La colección definitiva para transformar tu relación con las
                ventas. Sin presión. Sin máscaras. Con resultados.
              </motion.p>

              <motion.div variants={fadeIn} className="pt-4 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" data-id="element-36">
                <button className="group px-8 py-4 text-lg font-semibold text-[#1D1D1F] bg-gradient-to-r from-[#D1A047] to-[#C9A76A] rounded-full hover:shadow-2xl hover:shadow-[#D1A047]/40 transition-all duration-300 hover:-translate-y-1 flex items-center justify-center gap-2" data-id="element-37">
                  Acceder al Pack Premium
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-38" />
                </button>
                <button className="px-8 py-4 text-lg font-medium text-[#FAF7F3] border-2 border-[#D1A047]/40 rounded-full hover:bg-[#D1A047]/10 transition-all backdrop-blur-sm" data-id="element-39">
                  Ver contenido completo
                </button>
              </motion.div>

              <motion.div variants={fadeIn} className="flex items-center gap-8 justify-center lg:justify-start pt-4" data-id="element-40">
                <div className="flex items-center gap-2" data-id="element-41">
                  <div className="flex -space-x-2" data-id="element-42">
                    {[1, 2, 3, 4].map(i => <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D1A047] to-[#C9A76A] border-2 border-[#0F355E]" data-id="element-43"></div>)}
                  </div>
                  <span className="text-sm text-[#D8D1C6]" data-id="element-44">
                    +2,500 emprendedores
                  </span>
                </div>
                <div className="flex gap-1" data-id="element-45">
                  {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 fill-[#D1A047] text-[#D1A047]" data-id="element-46" />)}
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div initial={{
            opacity: 0,
            x: 50,
            rotateY: -15
          }} animate={{
            opacity: 1,
            x: 0,
            rotateY: 0
          }} transition={{
            duration: 1.2,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative perspective-1000" data-id="element-47">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-[#000]/50 border border-[#D1A047]/20" data-id="element-48">
                <div className="absolute inset-0 bg-gradient-to-tr from-[#D1A047]/20 to-transparent z-10 pointer-events-none" data-id="element-49"></div>
                <img src="https://cdn.magicpatterns.com/uploads/eD44QV2DVGGdFM2ozDaVok/imagen_3.png" alt="Pack Premium Vende sin Vender" className="w-full h-auto object-cover" data-id="element-50" />
              </div>
              {/* Glow effect */}
              <div className="absolute -inset-4 bg-gradient-to-r from-[#D1A047]/30 to-[#C9A76A]/30 rounded-3xl blur-3xl -z-10 opacity-60" data-id="element-51"></div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* TRUST BAR */}
      <section className="py-8 bg-[#FAF7F3] border-y border-[#D1A047]/10" data-id="element-52">
        <div className="max-w-7xl mx-auto px-6" data-id="element-53">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-id="element-54">
            {[{
            label: 'Garantía',
            value: '7 días',
            icon: ShieldCheck
          }, {
            label: 'Acceso',
            value: 'Inmediato',
            icon: Zap
          }, {
            label: 'Formato',
            value: 'Digital',
            icon: BookOpen
          }, {
            label: 'Soporte',
            value: 'Premium',
            icon: Award
          }].map((item, i) => <div key={i} className="flex flex-col items-center gap-2" data-id="element-55">
                <item.icon className="w-6 h-6 text-[#D1A047]" data-id="element-56" />
                <div className="text-2xl font-serif font-bold text-[#1D1D1F]" data-id="element-57">
                  {item.value}
                </div>
                <div className="text-sm text-[#444041]" data-id="element-58">{item.label}</div>
              </div>)}
          </div>
        </div>
      </section>

      {/* PAIN POINTS - Elegant Design */}
      <section className="py-24 px-6 bg-[#F5EFE6]" data-id="element-59">
        {/* Story Carousel Section */}
        <div className="max-w-5xl mx-auto mb-24" data-id="element-60">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-61">
            <span className="text-sm font-semibold tracking-wider text-[#D1A047] uppercase mb-4 block" data-id="element-62">
              Tu Historia de Transformación
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1D1D1F] mb-6 leading-tight" data-id="element-63">
              De la pena al método
            </h2>
            <p className="text-lg text-[#444041] max-w-3xl mx-auto leading-relaxed" data-id="element-64">
              Si te da pena escribirle de nuevo a tus clientes, no eres malo
              para las ventas: solo te falta un método sencillo.
            </p>
          </motion.div>

          {/* Interactive Carousel */}
          <div className="relative" data-id="element-65">
            <div className="overflow-hidden rounded-3xl border-2 border-[#D1A047]/20 shadow-2xl bg-white" data-id="element-66">
              <motion.div className="flex" animate={{
              x: ['0%', '-100%']
            }} transition={{
              duration: 60,
              repeat: Infinity,
              ease: 'linear'
            }} data-id="element-67">
                {[{
                img: 'https://cdn.magicpatterns.com/uploads/ofKDXXxktPHZEPGrnCHRyw/1c.png',
                alt: 'El problema no eres tú, problema es el método'
              }, {
                img: 'https://cdn.magicpatterns.com/uploads/aUSZuw8vnxCzhN4Kxumex4/2c.png',
                alt: 'Vende sin Vender en múltiples dispositivos'
              }, {
                img: 'https://cdn.magicpatterns.com/uploads/cVJiqDgTw6tZ2XqJm2HGSR/3c.png',
                alt: 'Antes y después del método'
              }, {
                img: 'https://cdn.magicpatterns.com/uploads/rxLpr1AjdUjAbTNiFxzvP1/4c.png',
                alt: 'Vende sin Vender - La fórmula de 30 días'
              }, {
                img: 'https://cdn.magicpatterns.com/uploads/fFuiaTLb2YqUh9rWZ5LuyX/5c.png',
                alt: 'El problema no eres tú'
              }, {
                img: 'https://cdn.magicpatterns.com/uploads/gLtvQJV9JfBAzYrijBMWKQ/6c.png',
                alt: 'Calendario: Antes y después'
              }, {
                img: 'https://cdn.magicpatterns.com/uploads/2cyHNnLdqqMDZYye5McN8v/7c.png',
                alt: 'Diario digital - Empezar mi reto de 30 días'
              }, {
                img: 'https://cdn.magicpatterns.com/uploads/kFH4a1MH1qNbjpW33jofA9/8c.png',
                alt: 'Emprendedores usando el método'
              }, {
                img: 'https://cdn.magicpatterns.com/uploads/j9oCsz1Md9LQoZmkMEjsoR/9c.png',
                alt: 'Sistema de seguimiento de clientes'
              }, {
                img: 'https://cdn.magicpatterns.com/uploads/b4gZ8ht6FLwWYBJeszpE31/10c.png',
                alt: 'Proceso de 5 pasos'
              }].map((slide, i) => <div key={i} className="flex-shrink-0 w-1/2 px-3" data-id="element-68">
                    <div className="relative w-full h-[400px] md:h-[500px] flex items-center justify-center" data-id="element-69">
                      <img src={slide.img} alt={slide.alt} className="max-w-full max-h-full object-contain rounded-xl" data-id="element-70" />
                    </div>
                  </div>)}
                {/* Duplicate for seamless loop */}
                {[{
                img: 'https://cdn.magicpatterns.com/uploads/ofKDXXxktPHZEPGrnCHRyw/1c.png',
                alt: 'El problema no eres tú, problema es el método'
              }, {
                img: 'https://cdn.magicpatterns.com/uploads/aUSZuw8vnxCzhN4Kxumex4/2c.png',
                alt: 'Vende sin Vender en múltiples dispositivos'
              }, {
                img: 'https://cdn.magicpatterns.com/uploads/cVJiqDgTw6tZ2XqJm2HGSR/3c.png',
                alt: 'Antes y después del método'
              }, {
                img: 'https://cdn.magicpatterns.com/uploads/rxLpr1AjdUjAbTNiFxzvP1/4c.png',
                alt: 'Vende sin Vender - La fórmula de 30 días'
              }, {
                img: 'https://cdn.magicpatterns.com/uploads/fFuiaTLb2YqUh9rWZ5LuyX/5c.png',
                alt: 'El problema no eres tú'
              }, {
                img: 'https://cdn.magicpatterns.com/uploads/gLtvQJV9JfBAzYrijBMWKQ/6c.png',
                alt: 'Calendario: Antes y después'
              }, {
                img: 'https://cdn.magicpatterns.com/uploads/2cyHNnLdqqMDZYye5McN8v/7c.png',
                alt: 'Diario digital - Empezar mi reto de 30 días'
              }, {
                img: 'https://cdn.magicpatterns.com/uploads/kFH4a1MH1qNbjpW33jofA9/8c.png',
                alt: 'Emprendedores usando el método'
              }, {
                img: 'https://cdn.magicpatterns.com/uploads/j9oCsz1Md9LQoZmkMEjsoR/9c.png',
                alt: 'Sistema de seguimiento de clientes'
              }, {
                img: 'https://cdn.magicpatterns.com/uploads/b4gZ8ht6FLwWYBJeszpE31/10c.png',
                alt: 'Proceso de 5 pasos'
              }].map((slide, i) => <div key={`dup-${i}`} className="flex-shrink-0 w-1/2 px-3" data-id="element-71">
                    <div className="relative w-full h-[400px] md:h-[500px] flex items-center justify-center" data-id="element-72">
                      <img src={slide.img} alt={slide.alt} className="max-w-full max-h-full object-contain rounded-xl" data-id="element-73" />
                    </div>
                  </div>)}
              </motion.div>
            </div>
          </div>

          {/* Story Description */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3
        }} className="mt-12 p-8 bg-gradient-to-br from-[#FAF7F3] to-[#F5EFE6] rounded-2xl border border-[#D8D1C6]" data-id="element-74">
            <p className="text-lg text-[#1D1D1F] leading-relaxed text-center max-w-3xl mx-auto" data-id="element-75">
              En esta presentación te muestro{' '}
              <span className="font-serif font-bold text-[#D1A047]" data-id="element-76">
                "Vende sin Vender"
              </span>
              , un ebook de 30 días que trabaja tu mente, tus mensajes y tus
              acciones en pasos pequeños. Semana a semana cambias miedo por
              claridad, aprendes a iniciar conversaciones reales, presentas tu
              oferta sin rogar y haces seguimiento sin sentir que molestas.
            </p>
            <div className="mt-6 pt-6 border-t border-[#D8D1C6]" data-id="element-77">
              <p className="text-base text-[#444041] text-center" data-id="element-78">
                <span className="font-semibold text-[#1D1D1F]" data-id="element-79">
                  Es para emprendedores de calle
                </span>{' '}
                que venden por WhatsApp, redes o catálogo y solo tienen 15
                minutos al día. Si quieres menos caos y más control en 30 días,
                este ebook es para ti.
              </p>
            </div>
          </motion.div>
        </div>

        <div className="max-w-4xl mx-auto text-center mb-16" data-id="element-80">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-81">
            <span className="text-sm font-semibold tracking-wider text-[#D1A047] uppercase mb-4 block" data-id="element-82">
              El Problema Real
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1D1D1F] mb-6 leading-tight" data-id="element-83">
              Si sientes que molestas cada vez que ofreces,
              <br data-id="element-84" />
              <span className="italic text-[#6C4A2F]" data-id="element-85">no estás solo</span>
            </h2>
            <p className="text-xl text-[#444041] font-light" data-id="element-86">
              El problema no eres tú. Es que nadie te enseñó a vender de forma
              auténtica.
            </p>
          </motion.div>
        </div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-87">
          {[{
          text: 'Publicas algo y casi nadie pregunta el precio',
          icon: MessageCircle
        }, {
          text: 'Te da pena hacer seguimiento porque no quieres molestar',
          icon: Heart
        }, {
          text: 'Ofreces descuentos por miedo a perder la venta',
          icon: DollarSign
        }, {
          text: 'Sientes que tu producto es bueno pero tú no sirves para vender',
          icon: Star
        }].map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="p-8 bg-[#FAF7F3] rounded-2xl border border-[#D8D1C6] hover:border-[#D1A047]/40 hover:shadow-lg transition-all duration-300" data-id="element-88">
              <div className="flex items-start gap-4" data-id="element-89">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#D1A047]/20 to-[#C9A76A]/20 flex items-center justify-center border border-[#D1A047]/20" data-id="element-90">
                  <item.icon className="w-6 h-6 text-[#D1A047]" data-id="element-91" />
                </div>
                <p className="text-lg text-[#1D1D1F] leading-relaxed font-medium" data-id="element-92">
                  {item.text}
                </p>
              </div>
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="max-w-3xl mx-auto mt-16 p-10 bg-gradient-to-br from-[#3A2A22] to-[#0F355E] rounded-3xl text-center shadow-2xl border border-[#D1A047]/20" data-id="element-93">
          <p className="text-2xl md:text-3xl font-serif italic text-[#FAF7F3] leading-relaxed" data-id="element-94">
            "El problema no eres tú. Es que nadie te enseñó a vender de forma
            simple, humana y sin máscaras."
          </p>
        </motion.div>
      </section>

      {/* TRANSFORMATION - Full Width Impact */}
      <section className="py-24 bg-gradient-to-b from-[#0C1F36] to-[#0F355E] overflow-hidden relative" data-id="element-95">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/paper.png')] opacity-5" data-id="element-96"></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-97">
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-98">
            <span className="text-sm font-semibold tracking-wider text-[#D1A047] uppercase mb-4 block" data-id="element-99">
              Tu Transformación
            </span>
            <h2 className="font-serif text-4xl md:text-6xl text-[#FAF7F3] mb-6" data-id="element-100">
              De la ansiedad a la claridad
            </h2>
            <p className="text-xl text-[#D8D1C6] max-w-2xl mx-auto font-light" data-id="element-101">
              Imagina 30 días después de hoy. Con más ingresos, más seguridad y
              una nueva identidad.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 1
        }} className="relative rounded-3xl overflow-hidden shadow-2xl border-2 border-[#D1A047]/30" data-id="element-102">
            <img src="https://cdn.magicpatterns.com/uploads/bizV6hZya3n8ecsEL6yx9M/imagen_1.png" alt="Transformación: Antes y Después" className="w-full h-auto" data-id="element-103" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F355E]/90 via-transparent to-transparent" data-id="element-104"></div>
            <div className="absolute bottom-0 left-0 w-full p-8 md:p-12" data-id="element-105">
              <p className="font-serif text-3xl md:text-4xl italic text-[#FAF7F3] text-center drop-shadow-2xl" data-id="element-106">
                "Ahora sí sé vender sin dejar de ser yo."
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SOLUTION SHOWCASE */}
      <section className="py-24 px-6 bg-[#FAF7F3]" data-id="element-107">
        <div className="max-w-7xl mx-auto" data-id="element-108">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-109">
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="relative" data-id="element-110">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#D1A047]/20 to-[#C9A76A]/20 rounded-3xl blur-2xl" data-id="element-111"></div>
              <img src="https://cdn.magicpatterns.com/uploads/dddVaueeMJ34PixbtZ3qQ6/imagen_6.png" alt="Pack Completo Vende sin Vender" className="relative w-full h-auto rounded-2xl shadow-2xl border border-[#D1A047]/20" data-id="element-112" />
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="space-y-8" data-id="element-113">
              <div data-id="element-114">
                <span className="text-sm font-semibold tracking-wider text-[#D1A047] uppercase mb-4 block" data-id="element-115">
                  La Solución Completa
                </span>
                <h2 className="font-serif text-4xl md:text-5xl text-[#1D1D1F] mb-6 leading-tight" data-id="element-116">
                  Un ebook premium y 6 ebooks exclusivos
                </h2>
                <p className="text-xl text-[#444041] leading-relaxed font-light" data-id="element-117">
                  El Pack Ventas "Vende sin Vender" reúne todo lo que necesitas
                  para transformar tu mentalidad, dominar las conversaciones y
                  crear ofertas irresistibles.
                </p>
              </div>

              <div className="space-y-4" data-id="element-118">
                {['Cambiar tu mentalidad sobre las ventas', 'Dominar el arte de la conversación natural', 'Hacer seguimiento sin sentir vergüenza', 'Crear ofertas y lanzamientos efectivos'].map((item, i) => <div key={i} className="flex items-center gap-4 p-4 bg-[#F5EFE6] rounded-xl border border-[#D8D1C6]" data-id="element-119">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-[#D1A047] to-[#C9A76A] flex items-center justify-center" data-id="element-120">
                      <Check className="w-4 h-4 text-[#FAF7F3]" data-id="element-121" />
                    </div>
                    <span className="text-lg text-[#1D1D1F] font-medium" data-id="element-122">
                      {item}
                    </span>
                  </div>)}
              </div>

              <button className="px-8 py-4 bg-gradient-to-r from-[#D1A047] to-[#C9A76A] text-[#1D1D1F] rounded-full font-semibold hover:shadow-xl hover:shadow-[#D1A047]/30 transition-all hover:-translate-y-1" data-id="element-123">
                Ver todo el contenido
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHAT'S INSIDE - Premium Gallery */}
      <section className="py-24 px-6 bg-gradient-to-b from-[#F5EFE6] to-[#FAF7F3]" data-id="element-124">
        <div className="max-w-7xl mx-auto" data-id="element-125">
          <div className="text-center mb-16" data-id="element-126">
            <span className="text-sm font-semibold tracking-wider text-[#D1A047] uppercase mb-4 block" data-id="element-127">
              Vista Interior
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1D1D1F] mb-6" data-id="element-128">
              Contenido de nivel premium
            </h2>
            <p className="text-xl text-[#444041] max-w-2xl mx-auto font-light" data-id="element-129">
              Cada página diseñada con cuidado para inspirarte a actuar
            </p>
          </div>

          {/* Main Preview */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="relative mb-16" data-id="element-130">
            <div className="absolute -inset-8 bg-gradient-to-r from-[#D1A047]/10 via-[#C9A76A]/10 to-[#D1A047]/10 rounded-[3rem] blur-3xl" data-id="element-131"></div>
            <img src="https://cdn.magicpatterns.com/uploads/dddVaueeMJ34PixbtZ3qQ6/imagen_6.png" alt="Páginas internas del diario" className="relative w-full h-auto rounded-3xl shadow-2xl border-2 border-[#D1A047]/20" data-id="element-132" />
          </motion.div>

          {/* Additional Views */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20" data-id="element-133">
            {[{
            src: 'https://cdn.magicpatterns.com/uploads/kiD14e55sMXq6Nwo4gRNhf/imagen_5.png',
            title: 'Calendario de 30 días + Sistema de notas'
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/dcrK43VBMRsHiFGWujV9xv/imagen_4.png',
            title: 'Checklist diaria + Identidad de vendedor'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.2
          }} className="group relative rounded-2xl overflow-hidden shadow-xl border border-[#D8D1C6] hover:border-[#D1A047]/40 transition-all duration-500" data-id="element-134">
                <img src={item.src} alt={item.title} className="w-full h-auto group-hover:scale-105 transition-transform duration-700" data-id="element-135" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1D1D1F]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6" data-id="element-136">
                  <p className="text-[#FAF7F3] font-serif text-xl" data-id="element-137">
                    {item.title}
                  </p>
                </div>
              </motion.div>)}
          </div>

          {/* Bonuses */}
          <div className="bg-gradient-to-br from-[#0F355E] to-[#3A2A22] rounded-3xl p-12 border border-[#D1A047]/20" data-id="element-138">
            <div className="text-center mb-12" data-id="element-139">
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-[#D1A047] rounded-full mb-6" data-id="element-140">
                <Sparkles className="w-5 h-5 text-[#1D1D1F]" data-id="element-141" />
                <span className="text-sm font-bold tracking-wide text-[#1D1D1F] uppercase" data-id="element-142">
                  Regalo de Lanzamiento
                </span>
              </div>
              <h3 className="font-serif text-3xl md:text-4xl text-[#FAF7F3] mb-4" data-id="element-143">
                6 Bonos Exclusivos Gratis
              </h3>
              <p className="text-lg text-[#D8D1C6] max-w-2xl mx-auto" data-id="element-144">
                Por tiempo limitado, llévate estos 6 ebooks premium
                completamente gratis con tu compra del ebook principal
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-145">
              {[{
              title: 'El Arte de Vender Conversando',
              subtitle: 'Para vender de forma natural, sin presionar al cliente',
              image: 'https://cdn.magicpatterns.com/uploads/48rs8WzwJACEZY1w5wpUpf/portada_bono-1.png',
              value: 'US$36'
            }, {
              title: 'Agenda de Seguimiento',
              subtitle: 'Piloto automático - Deja de perder ventas por olvido',
              image: 'https://cdn.magicpatterns.com/uploads/u5QGRqPbHn8gQ3MacbYbPd/portada_bono_-2.png',
              value: 'US$36'
            }, {
              title: 'Objeciones sin Miedo',
              subtitle: 'Conversaciones honestas que venden',
              image: 'https://cdn.magicpatterns.com/uploads/5WuzvzgEf2q6orU5C91Fpw/portada_bono_3.png',
              value: 'US$36'
            }, {
              title: 'Precios de Venta Justos',
              subtitle: 'Guía práctica para dejar de regalar tu trabajo',
              image: 'https://cdn.magicpatterns.com/uploads/eCotfSmxGpxhY1vVUfSkWK/portada_bono4.png',
              value: 'US$36'
            }, {
              title: 'Atrae Clientes',
              subtitle: 'Guía para no regalar tu trabajo y cobrar lo justo',
              image: 'https://cdn.magicpatterns.com/uploads/buj1hcXtbjEDhJ7T4jCzcv/portada_bono_5.png',
              value: 'US$36'
            }, {
              title: 'Plan de Lanzamiento Exprés',
              subtitle: 'Campaña estratégica de 7 días diseñada para impulsar las ventas',
              image: 'https://cdn.magicpatterns.com/uploads/4nWrszRbw7iYRfPDXXjAtT/portada_bono_6.png',
              value: 'US$36'
            }].map((bonus, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="group relative" data-id="element-146">
                  <div className="relative rounded-2xl overflow-hidden bg-[#FAF7F3] shadow-xl border border-[#D1A047]/20 hover:border-[#D1A047]/60 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2" data-id="element-147">
                    {/* Bonus Badge */}
                    <div className="absolute top-4 right-4 z-10 px-3 py-1 bg-[#D1A047] rounded-full" data-id="element-148">
                      <span className="text-xs font-bold text-[#1D1D1F]" data-id="element-149">
                        BONO {i + 1}
                      </span>
                    </div>

                    {/* Book Cover Image */}
                    <div className="aspect-[3/4] overflow-hidden bg-[#F5EFE6]" data-id="element-150">
                      <img src={bonus.image} alt={bonus.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-151" />
                    </div>

                    {/* Content */}
                    <div className="p-6" data-id="element-152">
                      <div className="flex items-center justify-between mb-3" data-id="element-153">
                        <span className="text-sm font-bold text-[#D1A047]" data-id="element-154">
                          Valor: {bonus.value}
                        </span>
                        <span className="text-xs font-bold text-[#1D1D1F] bg-[#D1A047]/20 px-2 py-1 rounded" data-id="element-155">
                          GRATIS
                        </span>
                      </div>
                      <h4 className="font-serif font-bold text-lg text-[#1D1D1F] mb-2 leading-tight" data-id="element-156">
                        {bonus.title}
                      </h4>
                      <p className="text-sm text-[#444041] leading-relaxed" data-id="element-157">
                        {bonus.subtitle}
                      </p>
                    </div>
                  </div>
                </motion.div>)}
            </div>

            {/* Total Value */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="mt-12 text-center" data-id="element-158">
              <div className="inline-block p-8 bg-[#FAF7F3]/10 backdrop-blur-sm rounded-2xl border border-[#D1A047]/30" data-id="element-159">
                <p className="text-[#D8D1C6] mb-2" data-id="element-160">
                  Valor total de los 7 ebooks:
                </p>
                <div className="flex items-center justify-center gap-4" data-id="element-161">
                  <span className="text-3xl font-serif text-[#D8D1C6] line-through" data-id="element-162">
                    US$252
                  </span>
                  <ArrowRight className="w-6 h-6 text-[#D1A047]" data-id="element-163" />
                  <span className="text-5xl font-serif font-bold bg-gradient-to-r from-[#D1A047] to-[#C9A76A] bg-clip-text text-transparent" data-id="element-164">
                    US$17
                  </span>
                </div>
                <p className="text-sm text-[#D8D1C6] mt-4" data-id="element-165">
                  Precio especial de lanzamiento • Ahorra US$235
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PRODUCT GALLERY - Dark Premium */}
      <section className="py-24 bg-gradient-to-br from-[#3A2A22] to-[#0C1F36] relative overflow-hidden" data-id="element-166">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/paper.png')] opacity-5" data-id="element-167"></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-168">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-169">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="space-y-8" data-id="element-170">
              <div data-id="element-171">
                <span className="text-sm font-semibold tracking-wider text-[#D1A047] uppercase mb-4 block" data-id="element-172">
                  Diseño Premium
                </span>
                <h2 className="font-serif text-4xl md:text-5xl text-[#FAF7F3] mb-6" data-id="element-173">
                  Calidad editorial que inspira acción
                </h2>
                <p className="text-xl text-[#D8D1C6] font-light leading-relaxed" data-id="element-174">
                  No es solo un PDF. Es una experiencia visual diseñada para que
                  quieras usarla cada día.
                </p>
              </div>

              <ul className="space-y-4" data-id="element-175">
                {['Formato imprimible de alta resolución', 'Diseño limpio y sin distracciones', 'Estructura probada de 30 días', 'Compatible con cualquier dispositivo'].map((item, i) => <li key={i} className="flex items-center gap-3 text-[#FAF7F3]" data-id="element-176">
                    <Star className="w-5 h-5 text-[#D1A047] fill-[#D1A047]" data-id="element-177" />
                    <span data-id="element-178">{item}</span>
                  </li>)}
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} className="relative" data-id="element-179">
              <div className="absolute -inset-6 bg-gradient-to-br from-[#D1A047]/30 to-[#C9A76A]/30 rounded-3xl blur-3xl" data-id="element-180"></div>
              <img src="https://cdn.magicpatterns.com/uploads/2V54TXnybwaBAAEyeUkaSw/imagen_2.png" alt="Libro premium fondo oscuro" className="relative w-full h-auto rounded-2xl shadow-2xl border border-[#D1A047]/30" data-id="element-181" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 px-6 bg-[#F5EFE6]" data-id="element-182">
        <div className="max-w-4xl mx-auto" data-id="element-183">
          <div className="text-center mb-16" data-id="element-184">
            <span className="text-sm font-semibold tracking-wider text-[#D1A047] uppercase mb-4 block" data-id="element-185">
              Proceso Simple
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1D1D1F] mb-6" data-id="element-186">
              ¿Cómo funciona en la práctica?
            </h2>
          </div>

          <div className="space-y-6" data-id="element-187">
            {['Descargas el pack completo desde MiKioscoDigital.com', 'Empiezas con el ebook durante 30 días consecutivos', 'Cada día aplicas una acción de venta concreta', 'Usas los bonos como apoyo: mensajes, seguimiento, objeciones', 'Repites lo que funciona y lo conviertes en tu sistema'].map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="flex items-center gap-6 p-6 bg-[#FAF7F3] rounded-2xl border border-[#D8D1C6] hover:border-[#D1A047]/40 hover:shadow-lg transition-all" data-id="element-188">
                <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-[#D1A047] to-[#C9A76A] flex items-center justify-center shadow-lg" data-id="element-189">
                  <span className="text-2xl font-serif font-bold text-[#1D1D1F]" data-id="element-190">
                    {i + 1}
                  </span>
                </div>
                <span className="text-lg text-[#1D1D1F] font-medium" data-id="element-191">
                  {step}
                </span>
              </motion.div>)}
          </div>

          <p className="text-center mt-12 text-[#444041] italic" data-id="element-192">
            No necesitas experiencia previa. Solo compromiso de 30 días.
          </p>
        </div>
      </section>

      {/* PRICING - Premium Offer */}
      <section className="py-24 px-6 bg-gradient-to-br from-[#0F355E] via-[#0C1F36] to-[#3A2A22] relative overflow-hidden" data-id="element-193">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/paper.png')] opacity-5" data-id="element-194"></div>
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D1A047]/10 rounded-full blur-[150px]" data-id="element-195"></div>

        <div className="max-w-6xl mx-auto relative z-10" data-id="element-196">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-197">
            {/* Left Column - Image */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="relative" data-id="element-198">
              <div className="absolute -inset-6 bg-gradient-to-br from-[#D1A047]/30 to-[#C9A76A]/30 rounded-3xl blur-3xl" data-id="element-199"></div>
              <img src="https://cdn.magicpatterns.com/uploads/aVgdSn4H8vPZvdX1in6kyd/1.png" alt="Pack Ventas: 7 ebooks para subir tus ingresos" className="relative w-full h-auto rounded-2xl shadow-2xl" data-id="element-200" />
            </motion.div>

            {/* Right Column - Content */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="space-y-8" data-id="element-201">
              <div data-id="element-202">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#D1A047] to-[#C9A76A] rounded-full mb-6" data-id="element-203">
                  <Sparkles className="w-4 h-4 text-[#1D1D1F]" data-id="element-204" />
                  <span className="text-sm font-bold tracking-wide text-[#1D1D1F] uppercase" data-id="element-205">
                    Oferta de Lanzamiento
                  </span>
                </div>
                <h2 className="font-serif text-4xl md:text-5xl text-[#FAF7F3] mb-4 leading-tight" data-id="element-206">
                  Pack Ventas: 7 ebooks para subir tus ingresos
                </h2>
                <p className="text-xl text-[#D8D1C6] leading-relaxed" data-id="element-207">
                  Todo lo que necesitas para vender con confianza, sin presión y
                  con resultados reales
                </p>
              </div>

              <div className="space-y-4" data-id="element-208">
                {['Vende sin Vender - Ebook de 30 días', 'El Arte de Vender Conversando', 'Agenda de Seguimiento en Piloto Automático', 'Objeciones sin Miedo', 'Precios de Venta Justos', 'Atrae Clientes', 'Plan de Lanzamiento Exprés'].map((item, i) => <div key={i} className="flex items-center gap-3" data-id="element-209">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-[#D1A047] to-[#C9A76A] flex items-center justify-center" data-id="element-210">
                      <Check className="w-4 h-4 text-[#1D1D1F]" data-id="element-211" />
                    </div>
                    <span className="text-[#FAF7F3] text-lg" data-id="element-212">{item}</span>
                  </div>)}
              </div>

              <div className="pt-6 border-t border-[#D1A047]/30" data-id="element-213">
                <div className="flex items-baseline gap-4 mb-4" data-id="element-214">
                  <span className="text-2xl text-[#D8D1C6] line-through font-serif" data-id="element-215">
                    US$79
                  </span>
                  <span className="text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-[#D1A047] to-[#C9A76A] bg-clip-text text-transparent" data-id="element-216">
                    US$17
                  </span>
                </div>
                <p className="text-sm text-[#D8D1C6] mb-6" data-id="element-217">
                  Precio especial de lanzamiento • Pago único • Acceso de por
                  vida
                </p>

                <button className="w-full sm:w-auto px-10 py-5 bg-gradient-to-r from-[#D1A047] to-[#C9A76A] text-[#1D1D1F] rounded-full font-bold text-lg hover:shadow-2xl hover:shadow-[#D1A047]/40 transition-all duration-300 hover:-translate-y-1 flex items-center justify-center gap-2" data-id="element-218">
                  Obtener Pack por US$17
                  <ArrowRight className="w-5 h-5" data-id="element-219" />
                </button>
              </div>

              <div className="flex items-center gap-6 pt-4" data-id="element-220">
                <div className="flex items-center gap-2" data-id="element-221">
                  <ShieldCheck className="w-5 h-5 text-[#D1A047]" data-id="element-222" />
                  <span className="text-sm text-[#D8D1C6]" data-id="element-223">
                    Garantía 7 días
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-224">
                  <Zap className="w-5 h-5 text-[#D1A047]" data-id="element-225" />
                  <span className="text-sm text-[#D8D1C6]" data-id="element-226">
                    Acceso inmediato
                  </span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 px-6 bg-[#F5EFE6] text-center" data-id="element-227">
        <div className="max-w-3xl mx-auto" data-id="element-228">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-229">
            <span className="text-sm font-semibold tracking-wider text-[#D1A047] uppercase mb-6 block" data-id="element-230">
              Tu Decisión
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1D1D1F] mb-8 leading-tight" data-id="element-231">
              30 días para transformar tu relación con las ventas
            </h2>
            <p className="text-xl text-[#444041] mb-12 font-light leading-relaxed" data-id="element-232">
              No necesitas nacer "vendedor". Necesitas un método probado. Este
              pack te da ese método, los mensajes y las herramientas. Tú solo
              pones la decisión.
            </p>

            <button className="inline-flex items-center justify-center px-12 py-5 bg-gradient-to-r from-[#D1A047] to-[#C9A76A] text-[#1D1D1F] rounded-full font-bold text-lg hover:shadow-2xl hover:shadow-[#D1A047]/40 transition-all duration-300 hover:-translate-y-1 gap-2" data-id="element-233">
              Empezar mi transformación ahora
              <ArrowRight className="w-5 h-5" data-id="element-234" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* FOOTER - Similar to image */}
      <footer className="py-16 px-6 bg-[#2C3440] text-[#D8D1C6]" data-id="element-235">
        <div className="max-w-7xl mx-auto" data-id="element-236">
          <div className="flex flex-col items-center space-y-8" data-id="element-237">
            {/* Brand with heart icon */}
            <div className="flex items-center gap-2 text-[#FAF7F3]" data-id="element-238">
              <Heart className="w-5 h-5 fill-[#D1A047] text-[#D1A047]" data-id="element-239" />
              <span className="text-lg font-medium" data-id="element-240">Vende sin Vender</span>
            </div>

            {/* Social Icons */}
            <div className="flex items-center gap-4" data-id="element-241">
              <a href="#" className="w-12 h-12 rounded-full bg-[#3A4450] hover:bg-[#D1A047] hover:text-white transition-all duration-300 hover:-translate-y-1" aria-label="Instagram" data-id="element-242">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-243">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" data-id="element-244" />
                </svg>
              </a>
              <a href="#" className="w-12 h-12 rounded-full bg-[#3A4450] hover:bg-[#D1A047] flex items-center justify-center transition-all duration-300 hover:-translate-y-1" aria-label="Facebook" data-id="element-245">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-246">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" data-id="element-247" />
                </svg>
              </a>
              <a href="#" className="w-12 h-12 rounded-full bg-[#3A4450] hover:bg-[#D1A047] flex items-center justify-center transition-all duration-300 hover:-translate-y-1" aria-label="YouTube" data-id="element-248">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-249">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" data-id="element-250" />
                </svg>
              </a>
            </div>

            {/* Navigation Links */}
            <nav className="flex flex-wrap items-center justify-center gap-6 md:gap-8 text-sm" data-id="element-251">
              <button onClick={() => setActiveModal('soporte')} className="hover:text-[#D1A047] transition-colors cursor-pointer" data-id="element-252">
                Soporte
              </button>
              <button onClick={() => setActiveModal('contacto')} className="hover:text-[#D1A047] transition-colors cursor-pointer" data-id="element-253">
                Contacto
              </button>
              <button onClick={() => setActiveModal('terminos')} className="hover:text-[#D1A047] transition-colors cursor-pointer" data-id="element-254">
                Términos
              </button>
              <button onClick={() => setActiveModal('privacidad')} className="hover:text-[#D1A047] transition-colors cursor-pointer" data-id="element-255">
                Privacidad
              </button>
            </nav>

            {/* Copyright */}
            <div className="text-center text-sm text-[#8A8A8A] pt-4 border-t border-[#3A4450] w-full" data-id="element-256">
              <p data-id="element-257">© 2024 Vende sin Vender • Todos los Derechos Reservados</p>
            </div>
          </div>
        </div>
      </footer>

      {/* MODALS */}
      <AnimatePresence data-id="element-258">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-[#1D1D1F]/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-259">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          opacity: 0,
          y: 20
        }} transition={{
          type: 'spring',
          duration: 0.5
        }} className="bg-[#FAF7F3] rounded-3xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto relative" onClick={e => e.stopPropagation()} data-id="element-260">
              {/* Close Button */}
              <button onClick={() => setActiveModal(null)} className="absolute top-6 right-6 w-10 h-10 rounded-full bg-[#F5EFE6] hover:bg-[#D1A047] hover:text-white transition-all flex items-center justify-center z-10" data-id="element-261">
                <X className="w-5 h-5" data-id="element-262" />
              </button>

              {/* Modal Content */}
              <div className="p-8 md:p-12" data-id="element-263">
                {activeModal === 'soporte' && <div className="space-y-6" data-id="element-264">
                    <div className="flex items-center gap-3 mb-6" data-id="element-265">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D1A047] to-[#C9A76A] flex items-center justify-center" data-id="element-266">
                        <HelpCircle className="w-6 h-6 text-[#1D1D1F]" data-id="element-267" />
                      </div>
                      <h2 className="font-serif text-3xl md:text-4xl text-[#1D1D1F]" data-id="element-268">
                        Centro de Soporte
                      </h2>
                    </div>

                    <p className="text-lg text-[#444041] leading-relaxed" data-id="element-269">
                      Estamos aquí para ayudarte a aprovechar al máximo tu Pack
                      "Vende sin Vender".
                    </p>

                    <div className="space-y-4 pt-4" data-id="element-270">
                      <div className="p-6 bg-[#F5EFE6] rounded-xl border border-[#D8D1C6]" data-id="element-271">
                        <h3 className="font-serif text-xl text-[#1D1D1F] mb-2 flex items-center gap-2" data-id="element-272">
                          <BookOpen className="w-5 h-5 text-[#D1A047]" data-id="element-273" />
                          Preguntas Frecuentes
                        </h3>
                        <ul className="space-y-3 text-[#444041] mt-4" data-id="element-274">
                          <li className="flex items-start gap-2" data-id="element-275">
                            <Check className="w-5 h-5 text-[#D1A047] flex-shrink-0 mt-0.5" data-id="element-276" />
                            <span data-id="element-277">
                              ¿Cómo accedo a mi pack después de la compra?
                              Recibirás un email con el enlace de descarga
                              inmediato.
                            </span>
                          </li>
                          <li className="flex items-start gap-2" data-id="element-278">
                            <Check className="w-5 h-5 text-[#D1A047] flex-shrink-0 mt-0.5" data-id="element-279" />
                            <span data-id="element-280">
                              ¿Puedo imprimir el ebook? Sí, todos los PDFs están
                              optimizados para impresión.
                            </span>
                          </li>
                          <li className="flex items-start gap-2" data-id="element-281">
                            <Check className="w-5 h-5 text-[#D1A047] flex-shrink-0 mt-0.5" data-id="element-282" />
                            <span data-id="element-283">
                              ¿Hay garantía de devolución? Sí, 7 días de
                              garantía sin preguntas.
                            </span>
                          </li>
                        </ul>
                      </div>

                      <div className="p-6 bg-[#F5EFE6] rounded-xl border border-[#D8D1C6]" data-id="element-284">
                        <h3 className="font-serif text-xl text-[#1D1D1F] mb-2 flex items-center gap-2" data-id="element-285">
                          <Mail className="w-5 h-5 text-[#D1A047]" data-id="element-286" />
                          ¿Necesitas más ayuda?
                        </h3>
                        <p className="text-[#444041] mt-2" data-id="element-287">
                          Escríbenos a{' '}
                          <a href="mailto:contactomikioscodigital@gmail.com" className="text-[#D1A047] font-medium hover:underline" data-id="element-288">
                            contactomikioscodigital@gmail.com
                          </a>{' '}
                          y te responderemos en menos de 24 horas.
                        </p>
                      </div>
                    </div>
                  </div>}

                {activeModal === 'contacto' && <div className="space-y-6" data-id="element-289">
                    <div className="flex items-center gap-3 mb-6" data-id="element-290">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D1A047] to-[#C9A76A] flex items-center justify-center" data-id="element-291">
                        <Mail className="w-6 h-6 text-[#1D1D1F]" data-id="element-292" />
                      </div>
                      <h2 className="font-serif text-3xl md:text-4xl text-[#1D1D1F]" data-id="element-293">
                        Contacto
                      </h2>
                    </div>

                    <p className="text-lg text-[#444041] leading-relaxed" data-id="element-294">
                      ¿Tienes alguna pregunta sobre el Pack "Vende sin Vender"?
                      Estamos aquí para ayudarte en cada paso de tu
                      transformación.
                    </p>

                    <div className="space-y-6 pt-4" data-id="element-295">
                      {/* Email Contact Card */}
                      <div className="p-8 bg-gradient-to-br from-[#F5EFE6] to-[#FAF7F3] rounded-2xl border-2 border-[#D1A047]/30 shadow-lg" data-id="element-296">
                        <div className="flex items-start gap-4 mb-6" data-id="element-297">
                          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#D1A047] to-[#C9A76A] flex items-center justify-center flex-shrink-0 shadow-md" data-id="element-298">
                            <Mail className="w-7 h-7 text-[#1D1D1F]" data-id="element-299" />
                          </div>
                          <div className="flex-1" data-id="element-300">
                            <h3 className="font-serif text-2xl text-[#1D1D1F] mb-2" data-id="element-301">
                              Email Principal
                            </h3>
                            <a href="mailto:contactomikioscodigital@gmail.com" className="text-xl text-[#D1A047] font-semibold hover:text-[#C9A76A] transition-colors break-all" data-id="element-302">
                              contactomikioscodigital@gmail.com
                            </a>
                            <p className="text-sm text-[#444041] mt-3 flex items-center gap-2" data-id="element-303">
                              <Check className="w-4 h-4 text-[#D1A047]" data-id="element-304" />
                              Respuesta garantizada en 24-48 horas
                            </p>
                          </div>
                        </div>

                        <div className="pt-6 border-t border-[#D8D1C6] space-y-3" data-id="element-305">
                          <h4 className="font-serif text-lg text-[#1D1D1F] mb-3" data-id="element-306">
                            Estamos para ayudarte con:
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-307">
                            {['Preguntas sobre el contenido del pack', 'Problemas con la descarga', 'Consultas sobre el método', 'Solicitudes de reembolso', 'Sugerencias y feedback', 'Soporte técnico general'].map((item, i) => <div key={i} className="flex items-start gap-2" data-id="element-308">
                                <Check className="w-4 h-4 text-[#D1A047] flex-shrink-0 mt-1" data-id="element-309" />
                                <span className="text-sm text-[#444041]" data-id="element-310">
                                  {item}
                                </span>
                              </div>)}
                          </div>
                        </div>
                      </div>

                      {/* Quick Tips */}
                      <div className="p-6 bg-[#F5EFE6] rounded-xl border border-[#D8D1C6]" data-id="element-311">
                        <h4 className="font-serif text-lg text-[#1D1D1F] mb-3 flex items-center gap-2" data-id="element-312">
                          <Sparkles className="w-5 h-5 text-[#D1A047]" data-id="element-313" />
                          Tips para contactarnos
                        </h4>
                        <ul className="space-y-2 text-sm text-[#444041]" data-id="element-314">
                          <li className="flex items-start gap-2" data-id="element-315">
                            <span className="text-[#D1A047] font-bold" data-id="element-316">•</span>
                            <span data-id="element-317">
                              Incluye tu nombre completo y email de compra
                            </span>
                          </li>
                          <li className="flex items-start gap-2" data-id="element-318">
                            <span className="text-[#D1A047] font-bold" data-id="element-319">•</span>
                            <span data-id="element-320">
                              Describe tu consulta con el mayor detalle posible
                            </span>
                          </li>
                          <li className="flex items-start gap-2" data-id="element-321">
                            <span className="text-[#D1A047] font-bold" data-id="element-322">•</span>
                            <span data-id="element-323">
                              Si es un problema técnico, adjunta capturas de
                              pantalla
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div className="pt-6 border-t border-[#D8D1C6]" data-id="element-324">
                      <div className="flex items-center justify-between" data-id="element-325">
                        <div data-id="element-326">
                          <h4 className="font-serif text-lg text-[#1D1D1F] mb-1" data-id="element-327">
                            Síguenos en redes sociales
                          </h4>
                          <p className="text-sm text-[#444041]" data-id="element-328">
                            Contenido exclusivo y actualizaciones
                          </p>
                        </div>
                        <div className="flex gap-3" data-id="element-329">
                          <a href="#" className="w-10 h-10 rounded-lg bg-[#F5EFE6] hover:bg-[#D1A047] hover:text-white transition-all flex items-center justify-center shadow-sm hover:shadow-md" data-id="element-330">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-331">
                              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" data-id="element-332" />
                            </svg>
                          </a>
                          <a href="#" className="w-10 h-10 rounded-lg bg-[#F5EFE6] hover:bg-[#D1A047] hover:text-white transition-all flex items-center justify-center shadow-sm hover:shadow-md" data-id="element-333">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-334">
                              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" data-id="element-335" />
                            </svg>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>}

                {activeModal === 'terminos' && <div className="space-y-6" data-id="element-336">
                    <div className="flex items-center gap-3 mb-6" data-id="element-337">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D1A047] to-[#C9A76A] flex items-center justify-center" data-id="element-338">
                        <ShieldCheck className="w-6 h-6 text-[#1D1D1F]" data-id="element-339" />
                      </div>
                      <h2 className="font-serif text-3xl md:text-4xl text-[#1D1D1F]" data-id="element-340">
                        Términos y Condiciones
                      </h2>
                    </div>

                    <p className="text-sm text-[#444041]" data-id="element-341">
                      Última actualización: Diciembre 2024
                    </p>

                    <div className="space-y-6 text-[#444041] leading-relaxed" data-id="element-342">
                      <div data-id="element-343">
                        <h3 className="font-serif text-xl text-[#1D1D1F] mb-3" data-id="element-344">
                          1. Aceptación de Términos
                        </h3>
                        <p data-id="element-345">
                          Al adquirir el Pack "Vende sin Vender", aceptas estos
                          términos y condiciones en su totalidad. Si no estás de
                          acuerdo, por favor no realices la compra.
                        </p>
                      </div>

                      <div data-id="element-346">
                        <h3 className="font-serif text-xl text-[#1D1D1F] mb-3" data-id="element-347">
                          2. Descripción del Producto
                        </h3>
                        <p data-id="element-348">
                          El Pack incluye 1 ebook de 30 días y 6 ebooks
                          complementarios en formato PDF. Todo el contenido es
                          digital y se entrega mediante descarga inmediata.
                        </p>
                      </div>

                      <div data-id="element-349">
                        <h3 className="font-serif text-xl text-[#1D1D1F] mb-3" data-id="element-350">
                          3. Licencia de Uso
                        </h3>
                        <p data-id="element-351">
                          Se otorga una licencia personal, no transferible, para
                          uso individual. No está permitido:
                        </p>
                        <ul className="list-disc list-inside mt-2 space-y-1 ml-4" data-id="element-352">
                          <li data-id="element-353">Revender o redistribuir el contenido</li>
                          <li data-id="element-354">Compartir los archivos con terceros</li>
                          <li data-id="element-355">Modificar el contenido para uso comercial</li>
                          <li data-id="element-356">Reclamar autoría del material</li>
                        </ul>
                      </div>

                      <div data-id="element-357">
                        <h3 className="font-serif text-xl text-[#1D1D1F] mb-3" data-id="element-358">
                          4. Política de Reembolso
                        </h3>
                        <p data-id="element-359">
                          Ofrecemos garantía de satisfacción de 7 días. Si no
                          estás satisfecho con tu compra, contáctanos dentro de
                          este período para solicitar un reembolso completo, sin
                          preguntas.
                        </p>
                      </div>

                      <div data-id="element-360">
                        <h3 className="font-serif text-xl text-[#1D1D1F] mb-3" data-id="element-361">
                          5. Propiedad Intelectual
                        </h3>
                        <p data-id="element-362">
                          Todo el contenido, diseño, textos e imágenes son
                          propiedad de Vende sin Vender y están protegidos por
                          leyes de derechos de autor.
                        </p>
                      </div>

                      <div data-id="element-363">
                        <h3 className="font-serif text-xl text-[#1D1D1F] mb-3" data-id="element-364">
                          6. Limitación de Responsabilidad
                        </h3>
                        <p data-id="element-365">
                          El contenido se proporciona "tal cual" con fines
                          educativos. No garantizamos resultados específicos de
                          ventas. El éxito depende de tu implementación y
                          circunstancias individuales.
                        </p>
                      </div>

                      <div data-id="element-366">
                        <h3 className="font-serif text-xl text-[#1D1D1F] mb-3" data-id="element-367">
                          7. Modificaciones
                        </h3>
                        <p data-id="element-368">
                          Nos reservamos el derecho de modificar estos términos
                          en cualquier momento. Los cambios se publicarán en
                          esta página con la fecha de actualización.
                        </p>
                      </div>
                    </div>

                    <div className="pt-6 border-t border-[#D8D1C6]" data-id="element-369">
                      <p className="text-sm text-[#444041]" data-id="element-370">
                        Para preguntas sobre estos términos, contáctanos en{' '}
                        <a href="mailto:contactomikioscodigital@gmail.com" className="text-[#D1A047] hover:underline" data-id="element-371">
                          contactomikioscodigital@gmail.com
                        </a>
                      </p>
                    </div>
                  </div>}

                {activeModal === 'privacidad' && <div className="space-y-6" data-id="element-372">
                    <div className="flex items-center gap-3 mb-6" data-id="element-373">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D1A047] to-[#C9A76A] flex items-center justify-center" data-id="element-374">
                        <ShieldCheck className="w-6 h-6 text-[#1D1D1F]" data-id="element-375" />
                      </div>
                      <h2 className="font-serif text-3xl md:text-4xl text-[#1D1D1F]" data-id="element-376">
                        Política de Privacidad
                      </h2>
                    </div>

                    <p className="text-sm text-[#444041]" data-id="element-377">
                      Última actualización: Diciembre 2024
                    </p>

                    <div className="space-y-6 text-[#444041] leading-relaxed" data-id="element-378">
                      <div data-id="element-379">
                        <h3 className="font-serif text-xl text-[#1D1D1F] mb-3" data-id="element-380">
                          1. Información que Recopilamos
                        </h3>
                        <p data-id="element-381">
                          Recopilamos la siguiente información cuando realizas
                          una compra:
                        </p>
                        <ul className="list-disc list-inside mt-2 space-y-1 ml-4" data-id="element-382">
                          <li data-id="element-383">Nombre completo</li>
                          <li data-id="element-384">Dirección de correo electrónico</li>
                          <li data-id="element-385">
                            Información de pago (procesada de forma segura por
                            nuestro proveedor)
                          </li>
                          <li data-id="element-386">Dirección IP y datos de navegación</li>
                        </ul>
                      </div>

                      <div data-id="element-387">
                        <h3 className="font-serif text-xl text-[#1D1D1F] mb-3" data-id="element-388">
                          2. Uso de la Información
                        </h3>
                        <p data-id="element-389">Utilizamos tu información para:</p>
                        <ul className="list-disc list-inside mt-2 space-y-1 ml-4" data-id="element-390">
                          <li data-id="element-391">Procesar y entregar tu compra</li>
                          <li data-id="element-392">Enviar confirmaciones y recibos</li>
                          <li data-id="element-393">Proporcionar soporte al cliente</li>
                          <li data-id="element-394">Mejorar nuestros productos y servicios</li>
                          <li data-id="element-395">
                            Enviar actualizaciones (solo si das tu
                            consentimiento)
                          </li>
                        </ul>
                      </div>

                      <div data-id="element-396">
                        <h3 className="font-serif text-xl text-[#1D1D1F] mb-3" data-id="element-397">
                          3. Protección de Datos
                        </h3>
                        <p data-id="element-398">
                          Implementamos medidas de seguridad técnicas y
                          organizativas para proteger tu información personal
                          contra acceso no autorizado, pérdida o alteración.
                        </p>
                      </div>

                      <div data-id="element-399">
                        <h3 className="font-serif text-xl text-[#1D1D1F] mb-3" data-id="element-400">
                          4. Compartir Información
                        </h3>
                        <p data-id="element-401">
                          No vendemos ni alquilamos tu información personal.
                          Solo compartimos datos con:
                        </p>
                        <ul className="list-disc list-inside mt-2 space-y-1 ml-4" data-id="element-402">
                          <li data-id="element-403">
                            Procesadores de pago (para completar transacciones)
                          </li>
                          <li data-id="element-404">
                            Proveedores de email (para enviar comunicaciones)
                          </li>
                          <li data-id="element-405">Autoridades legales (si es requerido por ley)</li>
                        </ul>
                      </div>

                      <div data-id="element-406">
                        <h3 className="font-serif text-xl text-[#1D1D1F] mb-3" data-id="element-407">
                          5. Cookies
                        </h3>
                        <p data-id="element-408">
                          Utilizamos cookies para mejorar tu experiencia de
                          navegación y analizar el tráfico del sitio. Puedes
                          desactivar las cookies en la configuración de tu
                          navegador.
                        </p>
                      </div>

                      <div data-id="element-409">
                        <h3 className="font-serif text-xl text-[#1D1D1F] mb-3" data-id="element-410">
                          6. Tus Derechos
                        </h3>
                        <p data-id="element-411">Tienes derecho a:</p>
                        <ul className="list-disc list-inside mt-2 space-y-1 ml-4" data-id="element-412">
                          <li data-id="element-413">Acceder a tu información personal</li>
                          <li data-id="element-414">Solicitar correcciones o eliminación</li>
                          <li data-id="element-415">Retirar tu consentimiento para comunicaciones</li>
                          <li data-id="element-416">
                            Presentar una queja ante autoridades de protección
                            de datos
                          </li>
                        </ul>
                      </div>

                      <div data-id="element-417">
                        <h3 className="font-serif text-xl text-[#1D1D1F] mb-3" data-id="element-418">
                          7. Retención de Datos
                        </h3>
                        <p data-id="element-419">
                          Conservamos tu información personal durante el tiempo
                          necesario para cumplir con los propósitos descritos, a
                          menos que la ley requiera un período de retención más
                          largo.
                        </p>
                      </div>

                      <div data-id="element-420">
                        <h3 className="font-serif text-xl text-[#1D1D1F] mb-3" data-id="element-421">
                          8. Cambios en la Política
                        </h3>
                        <p data-id="element-422">
                          Podemos actualizar esta política periódicamente. Te
                          notificaremos de cambios significativos por email o
                          mediante un aviso en nuestro sitio.
                        </p>
                      </div>
                    </div>

                    <div className="pt-6 border-t border-[#D8D1C6]" data-id="element-423">
                      <p className="text-sm text-[#444041]" data-id="element-424">
                        Para ejercer tus derechos o hacer preguntas sobre
                        privacidad, contáctanos en{' '}
                        <a href="mailto:contactomikioscodigital@gmail.com" className="text-[#D1A047] hover:underline" data-id="element-425">
                          contactomikioscodigital@gmail.com
                        </a>
                      </p>
                    </div>
                  </div>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}